// 设备常量Hook - 提供设备检测相关常量的React Hook
import { useState, useEffect, useCallback } from 'react';
import { 
  getDeviceConstants, 
  getDeviceTypeOptions,
  getClientTypeOptions,
  getOSTypeOptions,
  getBrowserTypeOptions,
  getDeviceBrandOptions,
  getNetworkTypeOptions,
  getRiskLevelOptions,
  getProxyTypeOptions,
  getLanguageCodeOptions,
  getBotTypeOptions,
  clearDeviceConstantsCache,
  type DeviceConstants
} from '../services/deviceConstants';

// Hook状态类型
interface DeviceConstantsState {
  constants: DeviceConstants | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  clearCache: () => void;
}

/**
 * 设备常量Hook
 * @param autoLoad 是否自动加载常量
 * @returns DeviceConstantsState
 */
export function useDeviceConstants(autoLoad: boolean = true): DeviceConstantsState {
  const [constants, setConstants] = useState<DeviceConstants | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadConstants = useCallback(async (forceRefresh: boolean = false) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getDeviceConstants(forceRefresh);
      setConstants(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load device constants');
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await loadConstants(true);
  }, [loadConstants]);

  const clearCache = useCallback(() => {
    clearDeviceConstantsCache();
    setConstants(null);
  }, []);

  useEffect(() => {
    if (autoLoad) {
      loadConstants();
    }
  }, [autoLoad, loadConstants]);

  return {
    constants,
    loading,
    error,
    refresh,
    clearCache
  };
}

// 设备类型选项Hook
export function useDeviceTypeOptions() {
  const [options, setOptions] = useState<Array<{value: string, label: string}>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await getDeviceTypeOptions();
        setOptions(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load device type options');
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, []);

  return { options, loading, error };
}

// 客户端类型选项Hook
export function useClientTypeOptions() {
  const [options, setOptions] = useState<Array<{value: string, label: string}>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await getClientTypeOptions();
        setOptions(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load client type options');
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, []);

  return { options, loading, error };
}

// 操作系统类型选项Hook
export function useOSTypeOptions() {
  const [options, setOptions] = useState<Array<{value: string, label: string}>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await getOSTypeOptions();
        setOptions(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load OS type options');
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, []);

  return { options, loading, error };
}

// 浏览器类型选项Hook
export function useBrowserTypeOptions() {
  const [options, setOptions] = useState<Array<{value: string, label: string}>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await getBrowserTypeOptions();
        setOptions(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load browser type options');
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, []);

  return { options, loading, error };
}

// 设备品牌选项Hook
export function useDeviceBrandOptions() {
  const [options, setOptions] = useState<Array<{value: string, label: string}>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await getDeviceBrandOptions();
        setOptions(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load device brand options');
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, []);

  return { options, loading, error };
}

// 网络类型选项Hook
export function useNetworkTypeOptions() {
  const [options, setOptions] = useState<Array<{value: string, label: string}>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await getNetworkTypeOptions();
        setOptions(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load network type options');
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, []);

  return { options, loading, error };
}

// 风险等级选项Hook
export function useRiskLevelOptions() {
  const [options, setOptions] = useState<Array<{value: string, label: string}>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await getRiskLevelOptions();
        setOptions(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load risk level options');
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, []);

  return { options, loading, error };
}

// 代理类型选项Hook
export function useProxyTypeOptions() {
  const [options, setOptions] = useState<Array<{value: string, label: string}>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await getProxyTypeOptions();
        setOptions(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load proxy type options');
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, []);

  return { options, loading, error };
}

// 语言代码选项Hook
export function useLanguageCodeOptions() {
  const [options, setOptions] = useState<Array<{value: string, label: string}>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await getLanguageCodeOptions();
        setOptions(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load language code options');
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, []);

  return { options, loading, error };
}

// 爬虫类型选项Hook
export function useBotTypeOptions() {
  const [options, setOptions] = useState<Array<{value: string, label: string}>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await getBotTypeOptions();
        setOptions(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load bot type options');
      } finally {
        setLoading(false);
      }
    };

    loadOptions();
  }, []);

  return { options, loading, error };
}
